import { FirebaseError } from 'firebase/app';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteField,
  query,
  where,
  serverTimestamp,
  writeBatch,
  type DocumentData,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { type OrgMember } from './org.service';

const USERS_COLLECTION = 'appUsers';

// ─── Types ────────────────────────────────────────────────────────────────────

export type Permission =
  | 'customers.read'
  | 'customers.write'
  | 'customers.delete'
  | 'cropTypes.manage'
  | 'regions.manage'
  | 'seasonTimes.manage'
  | 'cropStatus.read'
  | 'users.manage';

export const ALL_PERMISSIONS: Permission[] = [
  'customers.read',
  'customers.write',
  'customers.delete',
  'cropTypes.manage',
  'regions.manage',
  'seasonTimes.manage',
  'cropStatus.read',
  'users.manage',
];

export type AvatarColor = 'coral' | 'mint' | 'purple' | 'sky' | 'rose' | 'yellow' | 'sage' | 'peach' | 'teal';

export interface AppUser {
  uid: string;
  displayName: string;
  email: string;
  isDisabled: boolean;
  isSuperAdmin: boolean;
  avatarColor: AvatarColor;
  customAvatarColor?: string;
  lastSignIn: Timestamp;
  createdAt: Timestamp;
  schemaVersion?: number;
}

export interface AppRole {
  id: string;
  name: string;
  permissions: Permission[];
  isSystem: boolean;
  createdAt: Timestamp;
}

export interface AppUserServiceError {
  code: string;
  message: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function docToAppUser(uid: string, data: DocumentData): AppUser {
  return {
    uid,
    displayName: data.displayName as string,
    email: data.email as string,
    isDisabled: (data.isDisabled as boolean) ?? false,
    isSuperAdmin: (data.isSuperAdmin as boolean) ?? false,
    avatarColor: (data.avatarColor as AvatarColor) ?? 'mint',
    customAvatarColor: (data.customAvatarColor as string) ?? undefined,
    lastSignIn: data.lastSignIn as Timestamp,
    createdAt: data.createdAt as Timestamp,
  };
}

function docToAppRole(id: string, data: DocumentData): AppRole {
  return {
    id,
    name: data.name as string,
    permissions: (data.permissions ?? []) as Permission[],
    isSystem: data.isSystem as boolean,
    createdAt: data.createdAt as Timestamp,
  };
}

function docToOrgMember(uid: string, data: DocumentData): OrgMember {
  const member: OrgMember = {
    uid,
    role: data.role as string,
    permissions: (data.permissions ?? []) as string[],
    isDisabled: (data.isDisabled as boolean) ?? false,
    displayName: data.displayName as string,
    email: data.email as string,
    lastSignIn: data.lastSignIn as Timestamp,
    createdAt: data.createdAt as Timestamp,
    avatarColor: (data.avatarColor as string) ?? undefined,
    customAvatarColor: (data.customAvatarColor as string) ?? undefined,
  };
  return member;
}

function toServiceError(error: unknown): AppUserServiceError {
  if (error instanceof FirebaseError) {
    return { code: error.code, message: error.message };
  }
  return { code: 'unknown', message: 'An unexpected error occurred' };
}

// ─── Global AppUser profile functions ─────────────────────────────────────────

/**
 * Called on every Google sign-in.
 * Creates the appUsers doc if it doesn't exist (global profile only — no role or permissions),
 * or updates lastSignIn + display info on subsequent sign-ins.
 *
 * Role and permissions are now per-org, stored at orgs/{orgId}/members/{uid}.
 */
export async function getOrCreateAppUser(
  uid: string,
  displayName: string,
  email: string,
): Promise<AppUser> {
  try {
    const ref = doc(db, USERS_COLLECTION, uid);
    const snapshot = await getDoc(ref);

    if (!snapshot.exists()) {
      await setDoc(ref, {
        displayName,
        email,
        isDisabled: false,
        isSuperAdmin: false,
        avatarColor: 'mint',
        lastSignIn: serverTimestamp(),
        createdAt: serverTimestamp(),
      });
    } else {
      await updateDoc(ref, {
        lastSignIn: serverTimestamp(),
        displayName,
        email,
      });
    }

    const updated = await getDoc(ref);
    return docToAppUser(uid, updated.data()!);
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * One-time fetch of a single appUser doc.
 * Returns null if the doc doesn't exist yet.
 *
 * NOTE: We intentionally avoid onSnapshot here because the Firestore emulator
 * has a known bug — it corrupts its watch stream when security rules call
 * get(appUsers/uid) for other listeners while the client also has an onSnapshot
 * on the same document. A one-time fetch avoids the conflict; real-time role
 * changes take effect on the next sign-in or page refresh.
 */
export async function getAppUser(uid: string): Promise<AppUser | null> {
  try {
    const snapshot = await getDoc(doc(db, USERS_COLLECTION, uid));
    return snapshot.exists() ? docToAppUser(snapshot.id, snapshot.data()) : null;
  } catch (error) {
    throw toServiceError(error);
  }
}

/** Updates the avatar color for the current user and syncs to all org member docs. */
export async function updateAvatarColor(uid: string, color: AvatarColor, customHex?: string): Promise<void> {
  try {
    // Update appUsers doc
    const update: Record<string, unknown> = { avatarColor: color };
    if (customHex) {
      update.customAvatarColor = customHex;
    } else {
      // Clear custom color when using preset
      update.customAvatarColor = deleteField();
    }
    await updateDoc(doc(db, USERS_COLLECTION, uid), update);

    // Also sync avatar color to all org member docs
    try {
      const membershipSnap = await getDocs(collection(db, USERS_COLLECTION, uid, 'memberships'));
      const batch = writeBatch(db);
      membershipSnap.docs.forEach(membershipDoc => {
        const orgId = membershipDoc.id;
        const memberRef = doc(db, 'orgs', orgId, 'members', uid);
        const memberUpdate: Record<string, FieldValue | string> = { avatarColor: color };
        if (customHex) {
          memberUpdate.customAvatarColor = customHex;
        } else {
          memberUpdate.customAvatarColor = deleteField();
        }
        batch.update(memberRef, memberUpdate);
      });
      await batch.commit();
    } catch (syncError) {
      // If syncing to orgs fails, don't fail the whole operation
      // The appUser color was already updated, which is the critical part
      console.warn('Failed to sync avatar color to org members:', syncError);
    }
  } catch (error) {
    throw toServiceError(error);
  }
}

// ─── Org member management functions ─────────────────────────────────────────

/** Returns all members of an org. Requires users.manage permission. */
export async function getAllOrgMembers(orgId: string): Promise<OrgMember[]> {
  try {
    const snapshot = await getDocs(collection(db, 'orgs', orgId, 'members'));
    return snapshot.docs.map(d => docToOrgMember(d.id, d.data()));
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Assigns a new role to an org member and copies the role's permissions onto the member doc.
 * Requires users.manage permission (enforced by Firestore rules).
 */
export async function updateOrgMemberRole(orgId: string, uid: string, roleId: string): Promise<void> {
  try {
    const roleSnap = await getDoc(doc(db, 'orgs', orgId, 'roles', roleId));
    if (!roleSnap.exists()) {
      throw { code: 'not-found', message: `Role "${roleId}" does not exist.` };
    }
    const permissions = roleSnap.data().permissions as Permission[];
    await updateDoc(doc(db, 'orgs', orgId, 'members', uid), { role: roleId, permissions });
  } catch (error) {
    if ((error as AppUserServiceError).code) throw error;
    throw toServiceError(error);
  }
}

/** Disables or re-enables an org member. Requires users.manage permission. */
export async function setOrgMemberDisabled(orgId: string, uid: string, isDisabled: boolean): Promise<void> {
  try {
    await updateDoc(doc(db, 'orgs', orgId, 'members', uid), { isDisabled });
  } catch (error) {
    throw toServiceError(error);
  }
}

// ─── Org role functions ───────────────────────────────────────────────────────

/** Returns all roles in an org. Readable by any org member. */
export async function getAllOrgRoles(orgId: string): Promise<AppRole[]> {
  try {
    const snapshot = await getDocs(collection(db, 'orgs', orgId, 'roles'));
    return snapshot.docs.map(d => docToAppRole(d.id, d.data()));
  } catch (error) {
    throw toServiceError(error);
  }
}

/**
 * Updates a role's permissions and fans the change out to all org members with that role.
 * Requires users.manage permission (enforced by Firestore rules).
 */
export async function updateOrgRolePermissions(
  orgId: string,
  roleId: string,
  permissions: Permission[],
): Promise<void> {
  try {
    await updateDoc(doc(db, 'orgs', orgId, 'roles', roleId), { permissions });

    const q = query(collection(db, 'orgs', orgId, 'members'), where('role', '==', roleId));
    const membersSnap = await getDocs(q);
    await Promise.all(membersSnap.docs.map(d => updateDoc(d.ref, { permissions })));
  } catch (error) {
    throw toServiceError(error);
  }
}

/** Creates a new org role. Requires users.manage permission. */
export async function createOrgRole(
  orgId: string,
  id: string,
  name: string,
  permissions: Permission[],
): Promise<AppRole> {
  try {
    const ref = doc(db, 'orgs', orgId, 'roles', id);
    const existing = await getDoc(ref);
    if (existing.exists()) {
      throw { code: 'already-exists', message: `A role with id "${id}" already exists.` };
    }
    await setDoc(ref, {
      name,
      permissions,
      isSystem: false,
      createdAt: serverTimestamp(),
    });
    const snap = await getDoc(ref);
    return docToAppRole(snap.id, snap.data()!);
  } catch (error) {
    if ((error as AppUserServiceError).code) throw error;
    throw toServiceError(error);
  }
}
